import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress, Alert, Card, CardContent,
    Grid, Avatar, Tooltip, Tabs, Tab, Divider, TextField, MenuItem, FormControl, InputLabel, Select,
    useTheme, useMediaQuery, TablePagination, Drawer, List, ListItem, ListItemText, ListItemIcon,
    AppBar, Toolbar, Backdrop, Fade
} from '@mui/material';
import {
    Store, Person, Visibility as VisibilityIcon, ShoppingCart, Inventory, AttachMoney,
    Refresh, CheckCircle, Pending, Cancel, ExpandMore, ExpandLess, VideoLibrary, FilterList,
    Edit, ArrowBack, Close, Phone, Email, LocationOn, Business, AccountBalanceWallet,
    Star, TrendingUp, LocalShipping, Payment, ShoppingCartCheckout, Description, Category,
    Image as ImageIcon
} from '@mui/icons-material';
import adminService from '../services/adminService';

// Base URL for serving uploaded static files (images/videos)
const ASSET_BASE =
    process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://xaintro-backend.onrender.com';

const SellersManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const [sellers, setSellers] = useState([]);
    const [filteredSellers, setFilteredSellers] = useState([]);
    const [error, setError] = useState(null);
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [sellerDetails, setSellerDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [fullScreenDetails, setFullScreenDetails] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [orderFilter, setOrderFilter] = useState('all'); // 'all', 'pending', 'completed'
    const [expandedSeller, setExpandedSeller] = useState(null);
    const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
    const [videoDialogOpen, setVideoDialogOpen] = useState(false);
    const [sellerStatusFilter, setSellerStatusFilter] = useState('all'); // 'all', 'verified', 'pending', 'rejected'

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Edit Status State
    const [editStatusOpen, setEditStatusOpen] = useState(false);
    const [statusEditingSeller, setStatusEditingSeller] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [newPlan, setNewPlan] = useState('');
    const [newCnic, setNewCnic] = useState('');
    const [newCnicImage, setNewCnicImage] = useState('');
    const [newApprovedPaid, setNewApprovedPaid] = useState(false);
    const [viewImageOpen, setViewImageOpen] = useState(false);
    const [viewImageUrl, setViewImageUrl] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [togglingApprovedPaid, setTogglingApprovedPaid] = useState(false);

    const mapPlan = (oldPlan) => {
        const planMap = {
            starter: 'basic',
            pro: 'growth',
            free: 'free',
            basic: 'basic',
            growth: 'growth',
            business: 'business'
        };
        return planMap[oldPlan] || 'free';
    };

    const loadSellers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await adminService.getSellers();
            console.log('Sellers from backend:', response.sellers); // debug log
            const mappedSellers = (response.sellers || []).map(seller => ({
                ...seller,
                plan: mapPlan(seller.plan)
            }));
            setSellers(mappedSellers);
        } catch (err) {
            console.error('Failed to load sellers:', err);
            setError('Failed to load sellers');
        } finally {
            setLoading(false);
        }
    }, []);

    // Filter sellers based on status
    useEffect(() => {
        if (sellerStatusFilter === 'all') {
            setFilteredSellers(sellers);
        } else {
            setFilteredSellers(sellers.filter(seller => seller.sellerStatus === sellerStatusFilter));
        }
    }, [sellers, sellerStatusFilter]);

    useEffect(() => {
        loadSellers();
    }, [loadSellers]);

    const handleViewDetails = async (seller) => {
        setSelectedSeller(seller);
        setDetailsLoading(true);
        
        // Use full screen for mobile, dialog for desktop
        if (isMobile) {
            setFullScreenDetails(true);
        } else {
            setDetailsDialog(true);
        }
        
        setActiveTab(0);

        try {
            const response = await adminService.getSellerDetails(seller._id);
            console.log('Raw API response for seller details:', response); // Debug log for raw response
            
            // Backend returns { seller, products, orders, stats }
            // Create merged data with proper field mapping
            const mergedSellerData = {
                ...seller, // Original seller data from list
                ...response.seller, // Detailed seller data from API
                // Explicit field mapping to ensure all data is available
                shopName: response.seller?.brandName || seller?.brandName || seller?.shopName,
                businessDescription: response.seller?.storeDescription || response.seller?.businessDescription || seller?.storeDescription || seller?.businessDescription,
                location: response.seller?.location || seller?.location,
                storeUrl: response.seller?.storeUrl || seller?.storeUrl,
                plan: response.seller?.plan || seller?.plan,
                shopImage: response.seller?.shopImage || seller?.shopImage,
                codAvailable: response.seller?.codAvailable || seller?.codAvailable,
                fastDelivery: response.seller?.fastDelivery || seller?.fastDelivery,
                address: response.seller?.address || seller?.address,
                latitude: response.seller?.latitude || seller?.latitude,
                longitude: response.seller?.longitude || seller?.longitude,
                cnic: response.seller?.cnic || seller?.cnic,
                cnicImage: response.seller?.cnicImage || seller?.cnicImage,
                approvedPaid: response.seller?.approvedPaid !== undefined ? response.seller.approvedPaid : seller?.approvedPaid,
                approvedPaidAt: response.seller?.approvedPaidAt || seller?.approvedPaidAt,
                approvedPaidHistory: response.seller?.approvedPaidHistory || seller?.approvedPaidHistory || [],
                planStartDate: response.seller?.planStartDate || seller?.planStartDate,
                trialEndDate: response.seller?.trialEndDate || seller?.trialEndDate,
                paymentMethod: response.seller?.paymentMethod || seller?.paymentMethod,
                paymentProofImage: response.seller?.paymentProofImage || seller?.paymentProofImage,
                // Include stats, products, and orders from API
                stats: response.stats || {},
                products: response.products || [],
                orders: response.orders || []
            };
            setSellerDetails(mergedSellerData);
            console.log('Merged seller data:', mergedSellerData); // Debug log
        } catch (err) {
            console.error('Failed to load seller details:', err);
            setError('Failed to load seller details');
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleCloseDetails = () => {
        if (isMobile) {
            setFullScreenDetails(false);
        } else {
            setDetailsDialog(false);
        }
        setSelectedSeller(null);
        setSellerDetails(null);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'verified': return 'success';
            case 'pending': return 'warning';
            case 'rejected': return 'error';
            case 'new': return 'info';
            default: return 'default';
        }
    };

    const getOrderStatusColor = (status) => {
        switch (status) {
            case 'delivered': return 'success';
            case 'shipped': return 'info';
            case 'processing': return 'warning';
            case 'cancelled': return 'error';
            default: return 'warning';
        }
    };

    // Check if order is completed (delivered)
    const isOrderCompleted = (status) => {
        return status === 'delivered';
    };

    // Check if order is pending (not delivered or cancelled)
    const isOrderPending = (status) => {
        return status !== 'delivered' && status !== 'cancelled';
    };

    // Filter orders based on selected filter
    const getFilteredOrders = () => {
        if (!sellerDetails?.orders) return [];
        if (orderFilter === 'all') return sellerDetails.orders;
        if (orderFilter === 'completed') {
            return sellerDetails.orders.filter(order => isOrderCompleted(order.status));
        }
        if (orderFilter === 'pending') {
            return sellerDetails.orders.filter(order => isOrderPending(order.status));
        }
        return sellerDetails.orders;
    };

    // Calculate order stats
    const getOrderStats = () => {
        if (!sellerDetails?.orders) return { pending: 0, completed: 0, total: 0 };
        const pending = sellerDetails.orders.filter(order => isOrderPending(order.status)).length;
        const completed = sellerDetails.orders.filter(order => isOrderCompleted(order.status)).length;
        return { pending, completed, total: sellerDetails.orders.length };
    };

    const handleEditStatusClick = (seller) => {
        console.log('Editing seller:', seller); // debug log
        
        let planToUse = seller.plan;
        let cnicToUse = seller.cnic;
        let cnicImageToUse = seller.cnicImage;
        let approvedPaidToUse = seller.approvedPaid || false;
        if (selectedSeller && selectedSeller._id === seller._id && sellerDetails) {
            planToUse = sellerDetails.plan;
            if (sellerDetails.cnic) cnicToUse = sellerDetails.cnic;
            if (sellerDetails.cnicImage) cnicImageToUse = sellerDetails.cnicImage;
            if (sellerDetails.approvedPaid !== undefined) approvedPaidToUse = sellerDetails.approvedPaid;
        }
        
        setStatusEditingSeller(seller);
        setNewStatus(seller.sellerStatus || 'pending');
        setNewPlan(mapPlan(planToUse));
        setNewCnic(cnicToUse || '');
        setNewCnicImage(cnicImageToUse || '');
        setNewApprovedPaid(approvedPaidToUse);
        setEditStatusOpen(true);
    };

    const handleToggleApprovedPaid = async (seller, fromDetails = false) => {
        const sellerId = seller._id;
        setTogglingApprovedPaid(true);
        try {
            const result = await adminService.setApprovedPaid(sellerId);
            console.log('ApprovedPaid toggle result:', result);

            setSellers(sellers.map(s =>
                s._id === sellerId
                    ? {
                        ...s,
                        approvedPaid: result.seller.approvedPaid,
                        approvedPaidAt: result.seller.approvedPaidAt,
                        approvedPaidHistory: result.seller.approvedPaidHistory || s.approvedPaidHistory || [],
                        plan: mapPlan(result.seller.plan),
                        planStartDate: result.seller.planStartDate,
                        trialEndDate: result.seller.trialEndDate
                    }
                    : s
            ));

            if (fromDetails && selectedSeller && selectedSeller._id === sellerId) {
                setSelectedSeller(prev => prev ? {
                    ...prev,
                    approvedPaid: result.seller.approvedPaid,
                    approvedPaidAt: result.seller.approvedPaidAt,
                    approvedPaidHistory: result.seller.approvedPaidHistory || prev.approvedPaidHistory || [],
                    plan: mapPlan(result.seller.plan),
                    planStartDate: result.seller.planStartDate,
                    trialEndDate: result.seller.trialEndDate
                } : null);
                setSellerDetails(prev => prev ? {
                    ...prev,
                    approvedPaid: result.seller.approvedPaid,
                    approvedPaidAt: result.seller.approvedPaidAt,
                    approvedPaidHistory: result.seller.approvedPaidHistory || prev.approvedPaidHistory || [],
                    plan: mapPlan(result.seller.plan),
                    planStartDate: result.seller.planStartDate,
                    trialEndDate: result.seller.trialEndDate
                } : null);
            }
        } catch (err) {
            console.error('Failed to toggle approvedPaid:', err);
            alert('Failed to update Approved Paid status');
        } finally {
            setTogglingApprovedPaid(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!statusEditingSeller) return;

        setUpdatingStatus(true);
        try {
            const result = await adminService.updateSeller(statusEditingSeller._id, {
                status: newStatus,
                plan: newPlan,
                cnic: newCnic,
                cnicImage: newCnicImage,
                approvedPaid: newApprovedPaid
            });

            // Update local state
            setSellers(sellers.map(s =>
                s._id === statusEditingSeller._id
                    ? {
                        ...s,
                        sellerStatus: result.seller.sellerStatus,
                        role: result.seller.role,
                        plan: mapPlan(result.seller.plan),
                        cnic: result.seller.cnic,
                        cnicImage: result.seller.cnicImage,
                        approvedPaid: result.seller.approvedPaid,
                        planStartDate: result.seller.planStartDate,
                        trialEndDate: result.seller.trialEndDate
                    }
                    : s
            ));

            // Also update details if viewing
            if (selectedSeller && selectedSeller._id === statusEditingSeller._id) {
                setSelectedSeller({
                    ...selectedSeller,
                    sellerStatus: result.seller.sellerStatus,
                    plan: mapPlan(result.seller.plan),
                    cnic: result.seller.cnic,
                    cnicImage: result.seller.cnicImage,
                    approvedPaid: result.seller.approvedPaid,
                    planStartDate: result.seller.planStartDate,
                    trialEndDate: result.seller.trialEndDate
                });
                setSellerDetails(prev => prev ? {
                    ...prev,
                    sellerStatus: result.seller.sellerStatus,
                    plan: mapPlan(result.seller.plan),
                    cnic: result.seller.cnic,
                    cnicImage: result.seller.cnicImage,
                    approvedPaid: result.seller.approvedPaid,
                    planStartDate: result.seller.planStartDate,
                    trialEndDate: result.seller.trialEndDate
                } : null);
            }

            setEditStatusOpen(false);
            setStatusEditingSeller(null);
        } catch (err) {
            console.error('Failed to update seller:', err);
            alert('Failed to update seller information');
        } finally {
            setUpdatingStatus(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Store sx={{ fontSize: 32, color: '#FF5722' }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Sellers Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            View all sellers, their products, and orders
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label={`${filteredSellers.length} Sellers`} color="primary" sx={{ fontWeight: 600 }} />
                    <IconButton onClick={loadSellers}>
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Filter Section */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterList color="action" />
                        <Typography variant="subtitle2" fontWeight={600}>Filter Sellers:</Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={sellerStatusFilter}
                            label="Status"
                            onChange={(e) => setSellerStatusFilter(e.target.value)}
                        >
                            <MenuItem value="all">All Sellers</MenuItem>
                            <MenuItem value="verified">Verified</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                    </FormControl>
                    <Chip
                        label={`Showing ${filteredSellers.length} of ${sellers.length} sellers`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </Paper>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={2}>
                    <Card sx={{ bgcolor: '#e3f2fd' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Person sx={{ fontSize: 40, color: '#1976d2' }} />
                            <Typography variant="h4" fontWeight={700}>{sellers.length}</Typography>
                            <Typography variant="body2" color="text.secondary">Total Sellers</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Card sx={{ bgcolor: '#e8f5e9' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <CheckCircle sx={{ fontSize: 40, color: '#4CAF50' }} />
                            <Typography variant="h4" fontWeight={700}>
                                {sellers.filter(s => s.sellerStatus === 'verified').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Verified</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Card sx={{ bgcolor: '#fff3e0' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Inventory sx={{ fontSize: 40, color: '#FF9800' }} />
                            <Typography variant="h4" fontWeight={700}>
                                {sellers.reduce((sum, s) => sum + (s.stats?.totalProducts || 0), 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Products</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Card sx={{ bgcolor: '#e0f2fe' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={700}>
                                {sellers.filter(s => s.plan === 'free').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Free Plan</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Card sx={{ bgcolor: '#fef3c7' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={700}>
                                {sellers.filter(s => s.plan === 'basic').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Basic Plan</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Card sx={{ bgcolor: '#d1fae5' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={700}>
                                {sellers.filter(s => s.plan === 'growth').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Growth Plan</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Card sx={{ bgcolor: '#f3e8ff' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight={700}>
                                {sellers.filter(s => s.plan === 'business').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Business Plan</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={2}>
                    <Card sx={{ bgcolor: '#fce4ec' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <AttachMoney sx={{ fontSize: 40, color: '#E91E63' }} />
                            <Typography variant="h4" fontWeight={700}>
                                {sellers.reduce((sum, s) => sum + (s.stats?.totalRevenue || 0), 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Revenue (PKR)</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Sellers Table - Mobile Optimized */}
            {filteredSellers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Store sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No sellers registered yet
                    </Typography>
                </Paper>
            ) : (
                <TableContainer 
                    component={Paper}
                    sx={{ 
                        maxHeight: { xs: '400px', sm: 'none' },
                        overflow: { xs: 'auto', sm: 'visible' }
                    }}
                >
                    <Table size={isMobile ? "small" : "medium"}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Seller</TableCell>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Status</TableCell>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Plan</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Products</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Pending</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Orders</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Revenue</TableCell>
                                <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredSellers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((seller) => (
                                <React.Fragment key={seller._id}>
                                    <TableRow hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={(seller.profileImage || seller.shopImage) ? 
                                                        (seller.profileImage || seller.shopImage).startsWith('http') ? 
                                                            (seller.profileImage || seller.shopImage) : 
                                                            `${ASSET_BASE}${seller.profileImage || seller.shopImage}` : undefined}
                                                    sx={{ bgcolor: '#FF5722', width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}
                                                >
                                                    <Person sx={{ fontSize: isMobile ? 16 : 20 }} />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                                                        {seller.brandName || seller.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', display: { xs: 'none', sm: 'block' } }}>
                                                        {seller.email}
                                                    </Typography>
                                                    {/* Mobile status display */}
                                                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            label={seller.sellerStatus || 'pending'}
                                                            color={getStatusColor(seller.sellerStatus)}
                                                            size="small"
                                                            sx={{ fontSize: '0.6rem' }}
                                                        />
                                                        <Chip
                                                            label={seller.plan || 'free'}
                                                            size="small"
                                                            sx={{ fontSize: '0.6rem' }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={seller.approvedPaid ? '✓ PAID' : '⚠ UNAPPROVED'}
                                                            color={seller.approvedPaid ? 'success' : 'warning'}
                                                            variant="outlined"
                                                            sx={{ fontWeight: 600, fontSize: '0.6rem' }}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            PKR {(seller.stats?.totalRevenue || 0).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        {/* Desktop-only columns */}
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={seller.sellerStatus || 'pending'}
                                                    color={getStatusColor(seller.sellerStatus)}
                                                    size="small"
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditStatusClick(seller);
                                                    }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Chip
                                                    label={seller.plan || 'free'}
                                                    size="small"
                                                />
                                                <Chip
                                                    size="small"
                                                    label={seller.approvedPaid ? '✓ PAID' : '⚠ UNAPPROVED'}
                                                    color={seller.approvedPaid ? 'success' : 'warning'}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600, fontSize: '0.6rem' }}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Chip
                                                label={seller.stats?.totalProducts || 0}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Chip
                                                label={seller.stats?.pendingProducts || 0}
                                                color={seller.stats?.pendingProducts > 0 ? 'warning' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Chip
                                                label={seller.stats?.completedOrders || 0}
                                                color={seller.stats?.completedOrders > 0 ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Typography fontWeight={600}>
                                                PKR {(seller.stats?.totalRevenue || 0).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        {/* Actions - Always visible */}
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        onClick={() => handleViewDetails(seller)}
                                                        color="primary"
                                                        size={isMobile ? "small" : "medium"}
                                                        sx={{ 
                                                            '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' },
                                                            bgcolor: isMobile ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                                                        }}
                                                    >
                                                        <VisibilityIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {!isMobile && (
                                                    <IconButton
                                                        onClick={() => setExpandedSeller(expandedSeller === seller._id ? null : seller._id)}
                                                    >
                                                        {expandedSeller === seller._id ? <ExpandLess /> : <ExpandMore />}
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {/* Expanded Row - Desktop only */}
                                    {!isMobile && expandedSeller === seller._id && (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ bgcolor: '#fafafa' }}>
                                                <Grid container spacing={2} sx={{ p: 2 }}>
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant="caption" color="text.secondary">Approved Products</Typography>
                                                        <Typography variant="h6" color="success.main">{seller.stats?.approvedProducts || 0}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant="caption" color="text.secondary">Rejected Products</Typography>
                                                        <Typography variant="h6" color="error.main">{seller.stats?.rejectedProducts || 0}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                                                        <Typography variant="body2">{seller.phone || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant="caption" color="text.secondary">Joined</Typography>
                                                        <Typography variant="body2">
                                                            {seller.createdAt ? new Date(seller.createdAt).toLocaleDateString() : 'N/A'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            {filteredSellers.length > rowsPerPage && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredSellers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    sx={{ 
                        '.MuiTablePagination-toolbar': { 
                            fontSize: isMobile ? '0.8rem' : '0.875rem' 
                        }
                    }}
                />
            )}

            {/* Seller Details Dialog - Enhanced for Desktop */}
            <Dialog 
                open={detailsDialog} 
                onClose={() => setDetailsDialog(false)} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{
                    sx: {
                        maxHeight: '90vh',
                        m: 2
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    pb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Avatar sx={{ bgcolor: '#FF5722', width: 56, height: 56 }}>
                            <Store sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                {selectedSeller?.brandName || selectedSeller?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {selectedSeller?.email}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    label={selectedSeller?.sellerStatus || 'pending'}
                                    color={getStatusColor(selectedSeller?.sellerStatus)}
                                    size="small"
                                />
                                <Chip
                                    size="small"
                                    label={ (sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'PAID APPROVED' : 'NOT APPROVED' }
                                    color={ (sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'success' : 'warning' }
                                    variant={ (sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'filled' : 'outlined' }
                                    sx={{ fontWeight: 600 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Joined {selectedSeller?.createdAt ? new Date(selectedSeller.createdAt).toLocaleDateString() : 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Edit Status">
                            <IconButton
                                onClick={() => handleEditStatusClick(selectedSeller)}
                                sx={{ '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' } }}
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={() => setDetailsDialog(false)}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
                    {detailsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : sellerDetails ? (
                        <>
                            {/* Contact Information - Desktop */}
                            <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Business color="primary" />
                                    Contact Information
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Phone fontSize="small" color="action" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Phone</Typography>
                                                <Typography variant="body2">{sellerDetails?.phone || selectedSeller?.phone || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Person fontSize="small" color="action" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">CNIC</Typography>
                                                <Typography variant="body2">{sellerDetails?.cnic || selectedSeller?.cnic || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Store fontSize="small" color="action" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Shop Name</Typography>
                                                <Typography variant="body2">{sellerDetails?.shopName || selectedSeller?.shopName || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Email fontSize="small" color="action" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                                <Typography variant="body2">{sellerDetails?.email || selectedSeller?.email || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                                            <Box sx={{ width: 96, height: 64, borderRadius: 1, overflow: 'hidden', flexShrink: 0, border: 1, borderColor: 'grey.200' }}>
                                                {(sellerDetails?.cnicImage || selectedSeller?.cnicImage) ? (
                                                    <img src={sellerDetails.cnicImage || selectedSeller.cnicImage} alt="CNIC" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', bgcolor: 'grey.50' }}>
                                                        <ImageIcon fontSize="small" />
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="caption" color="text.secondary" display="block">CNIC / ID Card Image</Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {(sellerDetails?.cnicImage || selectedSeller?.cnicImage) ? 'CNIC image uploaded' : 'No CNIC image uploaded'}
                                                </Typography>
                                            </Box>
                                            {(sellerDetails?.cnicImage || selectedSeller?.cnicImage) && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => {
                                                        setViewImageUrl(sellerDetails.cnicImage || selectedSeller.cnicImage);
                                                        setViewImageOpen(true);
                                                    }}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    View
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Address</Typography>
                                                <Typography variant="body2">
                                                    {(() => {
                                                        const addressObj = sellerDetails.address || selectedSeller.address;
                                                        if (!addressObj || typeof addressObj !== 'object') {
                                                            return 'No address provided';
                                                        }
                                                        const parts = [
                                                            addressObj.street,
                                                            addressObj.city,
                                                            addressObj.zipCode,
                                                            addressObj.country
                                                        ].filter(Boolean);
                                                        return parts.length > 0 ? parts.join(', ') : 'No address provided';
                                                    })()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Business Information - Desktop */}
                            <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Business color="primary" />
                                    Business Information
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Store fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Shop Name</Typography>
                                                <Typography variant="body2">{sellerDetails?.shopName || selectedSeller?.shopName || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Category fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Business Category</Typography>
                                                <Typography variant="body2">{sellerDetails?.businessCategory || selectedSeller?.businessCategory || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <AccountBalanceWallet fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Plan</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2">{sellerDetails?.plan || selectedSeller?.plan || 'free'}</Typography>
                                                    <Chip
                                                        size="small"
                                                        label={(sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'PAID APPROVED' : 'NOT APPROVED'}
                                                        color={(sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'success' : 'default'}
                                                        variant="outlined"
                                                        sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Payment fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Trial Period</Typography>
                                                <Box>
                                                    {((sellerDetails?.planStartDate ?? selectedSeller?.planStartDate) && ['basic','growth','business'].includes(sellerDetails?.plan || selectedSeller?.plan)) ? (
                                                        <>
                                                            <Typography variant="body2">
                                                                Start: {new Date(sellerDetails?.planStartDate || selectedSeller?.planStartDate).toLocaleDateString()}
                                                            </Typography>
                                                            <Typography variant="caption" color={(sellerDetails?.trialEndDate ?? selectedSeller?.trialEndDate) && new Date() > new Date(sellerDetails?.trialEndDate || selectedSeller?.trialEndDate) ? 'error' : 'text.secondary'}>
                                                                End: {new Date(sellerDetails?.trialEndDate || selectedSeller?.trialEndDate).toLocaleDateString()}
                                                                {(sellerDetails?.trialEndDate ?? selectedSeller?.trialEndDate) && new Date() > new Date(sellerDetails?.trialEndDate || selectedSeller?.trialEndDate) ? ' (EXPIRED)' : ''}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <AccountBalanceWallet fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Bank Account</Typography>
                                                <Typography variant="body2">{sellerDetails?.bankAccount || selectedSeller?.bankAccount || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Payment fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                                                <Typography variant="body2">{sellerDetails?.paymentMethod || selectedSeller?.paymentMethod || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                                            <Box sx={{ width: 96, height: 64, borderRadius: 1, overflow: 'hidden', flexShrink: 0, border: 1, borderColor: 'grey.200' }}>
                                                {(sellerDetails?.paymentProofImage || selectedSeller?.paymentProofImage) ? (
                                                    <img src={sellerDetails.paymentProofImage || selectedSeller.paymentProofImage} alt="Payment Proof" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', bgcolor: 'grey.50' }}>
                                                        <ImageIcon fontSize="small" />
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="caption" color="text.secondary" display="block">Payment Proof Image</Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {(sellerDetails?.paymentProofImage || selectedSeller?.paymentProofImage) ? 'Payment proof uploaded' : 'No payment proof uploaded'}
                                                </Typography>
                                            </Box>
                                            {(sellerDetails?.paymentProofImage || selectedSeller?.paymentProofImage) && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => {
                                                        setViewImageUrl(sellerDetails.paymentProofImage || selectedSeller.paymentProofImage);
                                                        setViewImageOpen(true);
                                                    }}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    View
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <TrendingUp fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Experience</Typography>
                                                <Typography variant="body2">{sellerDetails?.experience || selectedSeller?.experience || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Star fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Rating</Typography>
                                                <Typography variant="body2">{sellerDetails?.rating || selectedSeller?.rating ? `${sellerDetails?.rating || selectedSeller?.rating}/5` : 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Description fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="caption" color="text.secondary">Business Description</Typography>
                                                <Typography variant="body2">{sellerDetails?.businessDescription || selectedSeller?.businessDescription || 'No description provided'}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Subscription Approval Section */}
                            <Paper sx={{ p: 3, mb: 3, bgcolor: (sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? '#e8f5e9' : '#fff8e1', border: 1, borderColor: (sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'success.main' : 'warning.main' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{
                                            width: 48, height: 48, borderRadius: 2,
                                            bgcolor: (sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'success.light' : 'warning.light',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            <Payment color={ (sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'success' : 'warning' } />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                Approved Paid
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                { (sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid)
                                                    ? 'Seller has approved paid subscription. Trial expiry will NOT downgrade plan.'
                                                    : 'Not approved yet. Seller will be downgraded to FREE plan after 3-day trial.'
                                                }
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        color={(sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'warning' : 'success'}
                                        size="medium"
                                        onClick={() => handleToggleApprovedPaid(selectedSeller, true)}
                                        disabled={togglingApprovedPaid}
                                        startIcon={togglingApprovedPaid ? <CircularProgress size={16} /> : ((sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? <Cancel /> : <CheckCircle />)}
                                        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 200 }}
                                    >
                                        {togglingApprovedPaid ? 'Updating...' : ((sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) ? 'Revoke Paid Approval' : 'Approve as Paid')}
                                    </Button>
                                    {(sellerDetails?.approvedPaid ?? selectedSeller?.approvedPaid) && (sellerDetails?.approvedPaidAt || selectedSeller?.approvedPaidAt) && (
                                        <Typography variant="caption" color="text.secondary" sx={{ width: '100%', textAlign: 'right' }}>
                                            Approved on: {new Date(sellerDetails?.approvedPaidAt || selectedSeller?.approvedPaidAt).toLocaleDateString()}
                                        </Typography>
                                    )}
                                    {((sellerDetails?.approvedPaidHistory || selectedSeller?.approvedPaidHistory || []).length > 0) && (
                                        <Typography variant="caption" color="text.secondary" sx={{ width: '100%', textAlign: 'right' }}>
                                            Approval records: {(sellerDetails?.approvedPaidHistory || selectedSeller?.approvedPaidHistory || []).map(record => new Date(record.approvedAt).toLocaleDateString()).join(', ')}
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>

                            {/* Stats Cards - Enhanced */}
                            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 3 }}>
                                <Grid item xs={4} sm={2}>
                                    <Card variant="outlined">
                                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"} 
                                                fontWeight={700}
                                                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                                            >
                                                {sellerDetails.stats?.totalProducts || 0}
                                            </Typography>
                                            <Typography 
                                                variant="caption"
                                                sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                            >
                                                Products
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"} 
                                                fontWeight={700} 
                                                color="warning.main"
                                                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                                            >
                                                {sellerDetails.stats?.pendingProducts || 0}
                                            </Typography>
                                            <Typography 
                                                variant="caption"
                                                sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                            >
                                                Pending
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"} 
                                                fontWeight={700} 
                                                color="success.main"
                                                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                                            >
                                                {sellerDetails.stats?.approvedProducts || 0}
                                            </Typography>
                                            <Typography 
                                                variant="caption"
                                                sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                            >
                                                Approved
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Card variant="outlined" sx={{ borderColor: 'info.main' }}>
                                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"} 
                                                fontWeight={700} 
                                                color="info.main"
                                                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                                            >
                                                {getOrderStats().total}
                                            </Typography>
                                            <Typography 
                                                variant="caption"
                                                sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                            >
                                                Orders
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"} 
                                                fontWeight={700} 
                                                color="warning.main"
                                                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                                            >
                                                {getOrderStats().pending}
                                            </Typography>
                                            <Typography 
                                                variant="caption"
                                                sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                            >
                                                Pending
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"} 
                                                fontWeight={700} 
                                                color="success.main"
                                                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                                            >
                                                {getOrderStats().completed}
                                            </Typography>
                                            <Typography 
                                                variant="caption"
                                                sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                            >
                                                Completed
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 3 }}>
                                <Grid item xs={12}>
                                    <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
                                        <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 2 } }}>
                                            <Typography 
                                                variant={isMobile ? "h6" : "h5"} 
                                                fontWeight={700} 
                                                color="primary.main"
                                                sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                                            >
                                                PKR {(sellerDetails.stats?.totalRevenue || 0).toLocaleString()}
                                            </Typography>
                                            <Typography 
                                                variant="caption"
                                                sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                            >
                                                Total Revenue
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Tabs - Mobile Optimized */}
                            <Tabs 
                                value={activeTab} 
                                onChange={(e, v) => setActiveTab(v)} 
                                sx={{ 
                                    mb: 2,
                                    '& .MuiTab-root': {
                                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                                        minWidth: isMobile ? 80 : 120
                                    }
                                }}
                                variant={isMobile ? "scrollable" : "standard"}
                                scrollButtons={isMobile ? "auto" : false}
                            >
                                <Tab label={`Products (${sellerDetails.products?.length || 0})`} />
                                <Tab label={`Orders (${sellerDetails.orders?.length || 0})`} />
                            </Tabs>

                            {/* Order Filter (only show when Orders tab is active) */}
                            {activeTab === 1 && (
                                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" fontWeight={600}>Filter Orders:</Typography>
                                    <Chip
                                        label="All"
                                        onClick={() => setOrderFilter('all')}
                                        color={orderFilter === 'all' ? 'primary' : 'default'}
                                        variant={orderFilter === 'all' ? 'filled' : 'outlined'}
                                        clickable
                                    />
                                    <Chip
                                        label={`Pending (${getOrderStats().pending})`}
                                        onClick={() => setOrderFilter('pending')}
                                        color={orderFilter === 'pending' ? 'warning' : 'default'}
                                        variant={orderFilter === 'pending' ? 'filled' : 'outlined'}
                                        clickable
                                    />
                                    <Chip
                                        label={`Completed (${getOrderStats().completed})`}
                                        onClick={() => setOrderFilter('completed')}
                                        color={orderFilter === 'completed' ? 'success' : 'default'}
                                        variant={orderFilter === 'completed' ? 'filled' : 'outlined'}
                                        clickable
                                    />
                                </Box>
                            )}

                            {activeTab === 0 && (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Product</TableCell>
                                                <TableCell>Category</TableCell>
                                                <TableCell>Price</TableCell>
                                                <TableCell>Video</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sellerDetails.products?.map((product) => (
                                                <TableRow key={product._id}>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Avatar
                                                                variant="rounded"
                                                                src={product.images?.[0] ?
                                                                    (product.images[0].startsWith('http') ? product.images[0] : `${ASSET_BASE}${product.images[0]}`)
                                                                    : undefined}
                                                                sx={{ width: 40, height: 40 }}
                                                            />
                                                            <Typography variant="body2">{product.name}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>{product.category?.name || 'N/A'}</TableCell>
                                                    <TableCell>PKR {product.price?.toLocaleString()}</TableCell>
                                                    <TableCell>
                                                        {product.video ? (
                                                            <Tooltip title="View product video">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => {
                                                                        const url = product.video.startsWith('http')
                                                                            ? product.video
                                                                            : `${ASSET_BASE}${product.video}`;
                                                                        setVideoPreviewUrl(url);
                                                                        setVideoDialogOpen(true);
                                                                    }}
                                                                >
                                                                    <VideoLibrary fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <Chip label="No video" size="small" variant="outlined" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={product.verificationStatus || 'pending'}
                                                            color={getStatusColor(product.verificationStatus === 'approved' ? 'verified' : product.verificationStatus)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(!sellerDetails.products || sellerDetails.products.length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3 }}>
                                                        No products yet
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {activeTab === 1 && (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Order #</TableCell>
                                                <TableCell>Customer</TableCell>
                                                <TableCell>Items</TableCell>
                                                <TableCell>Revenue</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {getFilteredOrders().map((order) => (
                                                <TableRow key={order._id}>
                                                    <TableCell>{order.orderNumber}</TableCell>
                                                    <TableCell>{order.customer?.name || 'Guest'}</TableCell>
                                                    <TableCell>{order.items?.length || 0}</TableCell>
                                                    <TableCell>PKR {(order.sellerTotal || 0).toLocaleString()}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={order.status || 'pending'}
                                                            color={getOrderStatusColor(order.status)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {getFilteredOrders().length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                                                        {orderFilter === 'all'
                                                            ? 'No orders yet'
                                                            : `No ${orderFilter === 'pending' ? 'pending' : 'completed'} orders`}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Product Video Preview Dialog */}
            <Dialog
                open={videoDialogOpen}
                onClose={() => setVideoDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Product Video</DialogTitle>
                <DialogContent dividers>
                    {videoPreviewUrl ? (
                        <Box sx={{ position: 'relative', pt: '56.25%' }}>
                            <Box
                                component="video"
                                src={videoPreviewUrl}
                                controls
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: 1,
                                    bgcolor: 'black'
                                }}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No video available.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVideoDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* CNIC / Image Viewer Dialog */}
            <Dialog
                open={viewImageOpen}
                onClose={() => setViewImageOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon color="primary" />
                    CNIC / ID Card Image
                </DialogTitle>
                <DialogContent dividers>
                    {viewImageUrl ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 1 }}>
                            <img
                                src={viewImageUrl}
                                alt="CNIC"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    borderRadius: 8,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                                }}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            No image available.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewImageOpen(false)} startIcon={<Close />}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Comprehensive Seller Edit Dialog */}
            <Dialog
                open={editStatusOpen}
                onClose={() => !updatingStatus && setEditStatusOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { maxHeight: '90vh' }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Edit color="primary" />
                    Edit Seller Information
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ pt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 3 }}>
                            Edit information for <b>{statusEditingSeller?.brandName || statusEditingSeller?.name}</b>
                        </Typography>
                        
                        <Grid container spacing={2}>
                            {/* Basic Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Basic Information</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={newStatus}
                                label="Status"
                                onChange={(e) => setNewStatus(e.target.value)}
                            >
                                <MenuItem value="new">New</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="verified">Verified</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Plan</InputLabel>
                            <Select
                                value={newPlan}
                                label="Plan"
                                onChange={(e) => setNewPlan(e.target.value)}
                            >
                                <MenuItem value="free">Free</MenuItem>
                                <MenuItem value="basic">Basic</MenuItem>
                                <MenuItem value="growth">Growth</MenuItem>
                                <MenuItem value="business">Business</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Phone"
                                    defaultValue={statusEditingSeller?.phone || ''}
                                    helperText="Contact phone number"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="CNIC"
                                    value={newCnic}
                                    onChange={(e) => setNewCnic(e.target.value)}
                                    placeholder="e.g. 35202-1234567-8"
                                    helperText="National ID number (13 digits)"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="CNIC Image URL"
                                    value={newCnicImage}
                                    onChange={(e) => setNewCnicImage(e.target.value)}
                                    placeholder="https://.../cnic.jpg"
                                    helperText="Cloudinary URL of CNIC/ID card image"
                                    InputProps={{
                                        endAdornment: newCnicImage ? (
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setViewImageUrl(newCnicImage);
                                                    setViewImageOpen(true);
                                                }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        ) : null
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Address"
                                    defaultValue={statusEditingSeller?.address || ''}
                                    helperText="Business address"
                                />
                            </Grid>
                            
                            {/* Business Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, mt: 2 }}>Business Information</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Shop Name"
                                    defaultValue={statusEditingSeller?.shopName || ''}
                                    helperText="Display shop name"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Business Category"
                                    defaultValue={statusEditingSeller?.businessCategory || ''}
                                    helperText="Type of business"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Business Description"
                                    defaultValue={statusEditingSeller?.businessDescription || ''}
                                    helperText="Describe the business"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Experience"
                                    defaultValue={statusEditingSeller?.experience || ''}
                                    helperText="Years of experience"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Rating"
                                    type="number"
                                    inputProps={{ min: 0, max: 5, step: 0.1 }}
                                    defaultValue={statusEditingSeller?.rating || ''}
                                    helperText="Rating out of 5"
                                />
                            </Grid>
                            
                            {/* Financial Information */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, mt: 2 }}>Financial Information</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Bank Account"
                                    defaultValue={statusEditingSeller?.bankAccount || ''}
                                    helperText="Bank account details"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Payment Method</InputLabel>
                                    <Select
                                        defaultValue={statusEditingSeller?.paymentMethod || ''}
                                        label="Payment Method"
                                    >
                                        <MenuItem value="bank">Bank Transfer</MenuItem>
                                        <MenuItem value="cash">Cash</MenuItem>
                                        <MenuItem value="online">Online Payment</MenuItem>
                                        <MenuItem value="mixed">Mixed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Subscription Approval */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, mt: 2 }}>Subscription Approval</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        bgcolor: newApprovedPaid ? '#e8f5e9' : '#fff8e1',
                                        borderColor: newApprovedPaid ? 'success.main' : 'warning.main'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                Approved Paid: {newApprovedPaid ? 'YES (Admin Approved)' : 'NO (Trial / Not Approved)'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {newApprovedPaid
                                                    ? 'Seller keeps paid features even after trial period.'
                                                    : 'Seller will be auto-downgraded to FREE plan + basic-store template after 3-day trial.'
                                                }
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant={newApprovedPaid ? 'outlined' : 'contained'}
                                            color={newApprovedPaid ? 'warning' : 'success'}
                                            size="small"
                                            onClick={() => setNewApprovedPaid(!newApprovedPaid)}
                                            startIcon={newApprovedPaid ? <Cancel /> : <CheckCircle />}
                                            sx={{ textTransform: 'none', fontWeight: 600 }}
                                        >
                                            {newApprovedPaid ? 'Mark as NOT Approved' : 'Mark as APPROVED'}
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setEditStatusOpen(false)} disabled={updatingStatus}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdateStatus}
                        variant="contained"
                        color="primary"
                        disabled={updatingStatus}
                        startIcon={updatingStatus ? <CircularProgress size={16} /> : <Edit />}
                    >
                        {updatingStatus ? 'Updating...' : 'Update Seller'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Full Screen Mobile Seller Details */}
            <Dialog
                open={fullScreenDetails}
                onClose={handleCloseDetails}
                fullScreen
                TransitionComponent={Fade}
                PaperProps={{
                    sx: { bgcolor: '#f8f9fa' }
                }}
            >
                <AppBar sx={{ position: 'relative', bgcolor: '#FF5722' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleCloseDetails}
                            aria-label="close"
                        >
                            <ArrowBack />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Seller Details
                        </Typography>
                        <IconButton
                            color="inherit"
                            onClick={() => handleEditStatusClick(selectedSeller)}
                        >
                            <Edit />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                
                <Box sx={{ p: 2, maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
                    {detailsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : selectedSeller && sellerDetails ? (
                        <>
                            {/* Seller Header Card */}
                            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Avatar
                                            src={(sellerDetails.profileImage || selectedSeller.profileImage) ? 
                                                (sellerDetails.profileImage || selectedSeller.profileImage).startsWith('http') ? 
                                                    (sellerDetails.profileImage || selectedSeller.profileImage) : 
                                                    `${ASSET_BASE}${sellerDetails.profileImage || selectedSeller.profileImage}` : 
                                                (sellerDetails.shopImage || selectedSeller.shopImage) ? 
                                                    (sellerDetails.shopImage || selectedSeller.shopImage).startsWith('http') ? 
                                                        (sellerDetails.shopImage || selectedSeller.shopImage) : 
                                                        `${ASSET_BASE}${sellerDetails.shopImage || selectedSeller.shopImage}` : undefined}
                                            sx={{ bgcolor: '#FF5722', width: 60, height: 60 }}
                                        >
                                            <Store sx={{ fontSize: 30 }} />
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h5" fontWeight={700} gutterBottom>
                                                {sellerDetails.brandName || sellerDetails.name || selectedSeller.brandName || selectedSeller.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {sellerDetails.email || selectedSeller.email}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={sellerDetails.sellerStatus || selectedSeller.sellerStatus || 'pending'}
                                                    color={getStatusColor(sellerDetails.sellerStatus || selectedSeller.sellerStatus)}
                                                    size="small"
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    Joined {sellerDetails.createdAt || selectedSeller.createdAt ? 
                                                        new Date(sellerDetails.createdAt || selectedSeller.createdAt).toLocaleDateString() : 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Contact Information */}
                                    <Grid container spacing={2} sx={{ mt: 2 }}>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {sellerDetails.phone || selectedSeller.phone || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Business fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {sellerDetails.shopName || selectedSeller.shopName || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                                                <Typography variant="body2">
                                                    {(() => {
                                                        const addressObj = sellerDetails.address || selectedSeller.address;
                                                        if (!addressObj || typeof addressObj !== 'object') {
                                                            return 'No address provided';
                                                        }
                                                        const parts = [
                                                            addressObj.street,
                                                            addressObj.city,
                                                            addressObj.zipCode,
                                                            addressObj.country
                                                        ].filter(Boolean);
                                                        return parts.length > 0 ? parts.join(', ') : 'No address provided';
                                                    })()}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccountBalanceWallet fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {sellerDetails.bankAccount || selectedSeller.bankAccount || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Payment fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {sellerDetails.paymentMethod || selectedSeller.paymentMethod || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <ImageIcon fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>Payment Proof</Typography>
                                            </Box>
                                            {(sellerDetails?.paymentProofImage || selectedSeller?.paymentProofImage) ? (
                                                <Box sx={{ ml: 3 }}>
                                                    <img
                                                        src={sellerDetails.paymentProofImage || selectedSeller.paymentProofImage}
                                                        alt="Payment Proof"
                                                        style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setViewImageUrl(sellerDetails.paymentProofImage || selectedSeller.paymentProofImage);
                                                            setViewImageOpen(true);
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" sx={{ ml: 3, color: 'text.secondary' }}>
                                                    No payment proof uploaded
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LocationOn fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {sellerDetails.location || selectedSeller.location || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Store fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {sellerDetails.plan || selectedSeller.plan || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Business Information Card */}
                            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Business sx={{ color: '#FF5722' }} />
                                        Business Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Store fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>Shop Details</Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ ml: 3 }}>
                                                {sellerDetails.shopName || selectedSeller.shopName || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Category fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>Business Category</Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ ml: 3 }}>
                                                {sellerDetails.businessCategory || selectedSeller.businessCategory || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Description fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>Business Description</Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ ml: 3 }}>
                                                {sellerDetails.businessDescription || selectedSeller.businessDescription || 'No description provided'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <TrendingUp fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>Experience</Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                {sellerDetails.experience || selectedSeller.experience || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Star fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>Rating</Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                {sellerDetails.rating || selectedSeller.rating ? 
                                                    `${sellerDetails.rating || selectedSeller.rating}/5` : 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Store fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>Store URL</Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                {sellerDetails.storeUrl || selectedSeller.storeUrl || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <LocalShipping fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>Fast Delivery</Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                {sellerDetails.fastDelivery || selectedSeller.fastDelivery ? 'Yes' : 'No'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <Payment fontSize="small" color="action" />
                                                <Typography variant="body2" fontWeight={600}>COD Available</Typography>
                                            </Box>
                                            <Typography variant="body2">
                                                {sellerDetails.codAvailable || selectedSeller.codAvailable ? 'Yes' : 'No'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Stats Cards Grid */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={6}>
                                    <Card sx={{ bgcolor: '#e3f2fd', textAlign: 'center' }}>
                                        <CardContent sx={{ py: 2 }}>
                                            <Inventory sx={{ fontSize: 30, color: '#1976d2', mb: 1 }} />
                                            <Typography variant="h5" fontWeight={700}>
                                                {sellerDetails.stats?.totalProducts || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">Products</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6}>
                                    <Card sx={{ bgcolor: '#fff3e0', textAlign: 'center' }}>
                                        <CardContent sx={{ py: 2 }}>
                                            <Pending sx={{ fontSize: 30, color: '#FF9800', mb: 1 }} />
                                            <Typography variant="h5" fontWeight={700}>
                                                {sellerDetails.stats?.pendingProducts || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">Pending</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6}>
                                    <Card sx={{ bgcolor: '#e8f5e9', textAlign: 'center' }}>
                                        <CardContent sx={{ py: 2 }}>
                                            <ShoppingCartCheckout sx={{ fontSize: 30, color: '#4CAF50', mb: 1 }} />
                                            <Typography variant="h5" fontWeight={700}>
                                                {getOrderStats().total}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">Orders</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6}>
                                    <Card sx={{ bgcolor: '#fce4ec', textAlign: 'center' }}>
                                        <CardContent sx={{ py: 2 }}>
                                            <AttachMoney sx={{ fontSize: 30, color: '#E91E63', mb: 1 }} />
                                            <Typography variant="h5" fontWeight={700}>
                                                PKR {(sellerDetails.stats?.totalRevenue || 0).toLocaleString()}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">Revenue</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Tabs for Products and Orders */}
                            <Paper sx={{ borderRadius: 3 }}>
                                <Tabs 
                                    value={activeTab} 
                                    onChange={(e, v) => setActiveTab(v)} 
                                    variant="fullWidth"
                                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                                >
                                    <Tab label={`Products (${sellerDetails.products?.length || 0})`} />
                                    <Tab label={`Orders (${sellerDetails.orders?.length || 0})`} />
                                </Tabs>

                                <Box sx={{ p: 2 }}>
                                    {activeTab === 0 && (
                                        <Box>
                                            {sellerDetails.products?.map((product) => (
                                                <Card key={product._id} sx={{ mb: 2, borderRadius: 2 }}>
                                                    <CardContent sx={{ p: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar
                                                                variant="rounded"
                                                                src={product.images?.[0] ?
                                                                    (product.images[0].startsWith('http') ? product.images[0] : `${ASSET_BASE}${product.images[0]}`)
                                                                    : undefined}
                                                                sx={{ width: 50, height: 50 }}
                                                            />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="subtitle1" fontWeight={600}>
                                                                    {product.name}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {product.category?.name || 'N/A'}
                                                                </Typography>
                                                                <Typography variant="h6" color="primary" fontWeight={700}>
                                                                    PKR {product.price?.toLocaleString()}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ textAlign: 'right' }}>
                                                                <Chip
                                                                    label={product.verificationStatus || 'pending'}
                                                                    color={getStatusColor(product.verificationStatus === 'approved' ? 'verified' : product.verificationStatus)}
                                                                    size="small"
                                                                    sx={{ mb: 1 }}
                                                                />
                                                                {product.video && (
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => {
                                                                            const url = product.video.startsWith('http')
                                                                                ? product.video
                                                                                : `${ASSET_BASE}${product.video}`;
                                                                            setVideoPreviewUrl(url);
                                                                            setVideoDialogOpen(true);
                                                                        }}
                                                                    >
                                                                        <VideoLibrary fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                            {(!sellerDetails.products || sellerDetails.products.length === 0) && (
                                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography color="text.secondary">No products yet</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {activeTab === 1 && (
                                        <Box>
                                            {/* Order Filter */}
                                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label="All"
                                                    onClick={() => setOrderFilter('all')}
                                                    color={orderFilter === 'all' ? 'primary' : 'default'}
                                                    variant={orderFilter === 'all' ? 'filled' : 'outlined'}
                                                    size="small"
                                                />
                                                <Chip
                                                    label={`Pending (${getOrderStats().pending})`}
                                                    onClick={() => setOrderFilter('pending')}
                                                    color={orderFilter === 'pending' ? 'warning' : 'default'}
                                                    variant={orderFilter === 'pending' ? 'filled' : 'outlined'}
                                                    size="small"
                                                />
                                                <Chip
                                                    label={`Completed (${getOrderStats().completed})`}
                                                    onClick={() => setOrderFilter('completed')}
                                                    color={orderFilter === 'completed' ? 'success' : 'default'}
                                                    variant={orderFilter === 'completed' ? 'filled' : 'outlined'}
                                                    size="small"
                                                />
                                            </Box>

                                            {getFilteredOrders().map((order) => (
                                                <Card key={order._id} sx={{ mb: 2, borderRadius: 2 }}>
                                                    <CardContent sx={{ p: 2 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                Order #{order.orderNumber}
                                                            </Typography>
                                                            <Chip
                                                                label={order.status || 'pending'}
                                                                color={getOrderStatusColor(order.status)}
                                                                size="small"
                                                            />
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                                            {order.customer?.name || 'Guest'} • {order.items?.length || 0} items
                                                        </Typography>
                                                        <Typography variant="h6" color="primary" fontWeight={700}>
                                                            PKR {(order.sellerTotal || 0).toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                            {getFilteredOrders().length === 0 && (
                                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography color="text.secondary">
                                                        {orderFilter === 'all'
                                                            ? 'No orders yet'
                                                            : `No ${orderFilter === 'pending' ? 'pending' : 'completed'} orders`}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">No seller details available</Typography>
                        </Box>
                    )}
                </Box>
            </Dialog>
        </Box>
    );
};

export default SellersManagement;
